// Trích xuất link ảnh/video/audio từ output JSON (mọi provider có shape khác nhau:
// image_urls, images, resultUrls, result_url, output_url, video_url, thumbnail_url...).
// Đi đệ quy toàn bộ object/array. Mỗi URL được phân loại bằng TỔNG HỢP bằng chứng:
//   - tên khóa (key) chứa từ gợi ý (video / image / result_url / output_url...),
//   - đuôi file trong đường dẫn (.png .mp4...),
//   - tham số format trong query (vd ...?format=png,quality=100).
// Nếu cùng 1 URL xuất hiện dưới nhiều khóa (vd result_url + video_url) thì ưu tiên
// video > audio > image để không render nhầm video thành ảnh hỏng.

export interface ExtractedMedia {
  images: string[];
  videos: string[];
  audios: string[];
}

const IMG_EXT = /\.(png|jpe?g|webp|gif|bmp|svg|avif|heic|tiff?)(\?|#|$)/i;
const VID_EXT = /\.(mp4|webm|mov|m4v|avi|mkv|m3u8)(\?|#|$)/i;
const AUD_EXT = /\.(mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/i;

// Gợi ý qua query (vd cloudflare imagedelivery: .../format=png,quality=100)
const IMG_FMT = /\b(png|jpe?g|webp|gif|bmp|svg|avif|heic)\b/i;
const VID_FMT = /\b(mp4|webm|mov|m4v|avi|mkv)\b/i;

// Gợi ý qua tên khóa
const KEY_VIDEO = /video/i;
const KEY_AUDIO = /audio|sound|voice|music/i;
const KEY_IMAGE = /image|img|photo|picture|thumb|avatar|banner|poster|cover/i;
// Khóa "có vẻ là link media" (mặc định coi là ảnh nếu không có dấu hiệu video/audio).
const KEY_MEDIA = /src|link|file|media|download|asset|result|output|cdn/i;
const SKIP_KEYS = new Set(['raw', '__gwMeta', 'request', 'body', 'endpoint', 'base_url', 'baseUrl']);
const SKIP_TEXT_KEYS = new Set(['raw', '__gwMeta', 'revised_prompt', 'revisedPrompt']);

function isUrl(s: string): boolean {
  return /^https?:\/\//i.test(s) || /^data:(image|video|audio)\//i.test(s);
}

function classify(url: string, hint: string): keyof ExtractedMedia | null {
  const query = url.split('?')[1] ?? '';
  // 1) Video
  if (KEY_VIDEO.test(hint) || VID_EXT.test(url) || VID_FMT.test(query)) return 'videos';
  // 2) Audio
  if (KEY_AUDIO.test(hint) || AUD_EXT.test(url)) return 'audios';
  // 3) Ảnh
  if (KEY_IMAGE.test(hint) || IMG_EXT.test(url) || IMG_FMT.test(query) || /^data:image\//i.test(url))
    return 'images';
  // 4) Link media chung (result_url, output_url, url, src...) -> mặc định ảnh
  if (KEY_MEDIA.test(hint) || hint === '') return 'images';
  return null; // URL không rõ loại & khóa không gợi ý media -> bỏ qua
}

/** Đi đệ quy output, gom mọi URL media (gộp gợi ý theo URL), giữ thứ tự xuất hiện. */
export function extractMedia(value: unknown): ExtractedMedia {
  const out: ExtractedMedia = { images: [], videos: [], audios: [] };
  // url -> tổng hợp các tên khóa đã gặp (để quyết định loại sau khi đi hết).
  const hints = new Map<string, string>();

  const walk = (v: unknown, keyHint: string) => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (!isUrl(v)) return;
      hints.set(v, (hints.get(v) ?? '') + ' ' + keyHint);
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) walk(item, keyHint);
      return;
    }
    if (typeof v === 'object') {
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        if (SKIP_KEYS.has(k)) continue;
        walk(val, k);
      }
    }
  };
  walk(value, '');

  for (const [url, hint] of hints) {
    const bucket = classify(url, hint.trim());
    if (bucket) out[bucket].push(url);
  }
  return out;
}

export function hasMedia(m: ExtractedMedia): boolean {
  return m.images.length > 0 || m.videos.length > 0 || m.audios.length > 0;
}

/** Lấy đoạn text gọn từ output để xem nhanh ở danh sách (chat/text result). */
export function extractText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const candidates: string[] = [];
  const walk = (v: unknown, keyHint: string) => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (isUrl(v)) return;
      if (/text|content|message|answer|caption|result|output|prompt|description|reply/i.test(keyHint)) {
        candidates.push(v);
      }
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) walk(item, keyHint);
      return;
    }
    if (typeof v === 'object') {
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        if (SKIP_TEXT_KEYS.has(k)) continue;
        walk(val, k);
      }
    }
  };
  walk(value, '');
  // Loại trùng, gộp lại.
  return Array.from(new Set(candidates)).join('\n').trim();
}
